import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Upload, Trash2, Save, Image as ImageIcon, Crown, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const SOCIAL_FIELDS = [
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourhandle' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@yourchannel' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@yourhandle' },
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/yourpage' },
  { key: 'whatsapp', label: 'WhatsApp Channel', placeholder: 'https://whatsapp.com/channel/...' },
  { key: 'discord', label: 'Discord', placeholder: 'https://discord.gg/yourserver' },
  { key: 'telegram', label: 'Telegram', placeholder: 'https://t.me/yourchannel' },
  { key: 'website', label: 'Website', placeholder: 'https://yoursite.com' },
  { key: 'support_email', label: 'Support Email', placeholder: 'support@yoursite.com' },
];

export default function BrandingPage() {
  const [data, setData] = useState({
    logo_url: '', brand_name: '', brand_tagline: '', description: '',
    verified: true,
    preferred_support_channel: '',
    primary_color: '#10B981',
    stats: { followers: '', games: '', rewards_claimed: '' },
    socials: Object.fromEntries(SOCIAL_FIELDS.map(f => [f.key, ''])),
    support: {
      enabled: true,
      headline: "Forgot your username or password? We're here to help.",
      subheadline: 'Reach us on any channel — we reply 24/7.',
      telegram_url: '', whatsapp_url: '', messenger_url: '',
      facebook_url: '', email: '', phone: '', discord_url: '',
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/api/admin/branding').then(r => setData(d => ({
      ...d, ...(r.data || {}),
      support: { ...d.support, ...((r.data || {}).support || {}) },
      socials: { ...d.socials, ...((r.data || {}).socials || {}) },
    })))
      .catch(() => toast.error('Failed to load branding'))
      .finally(() => setLoading(false));
  }, []);

  const setSupport = (k, v) => setData(d => ({ ...d, support: { ...d.support, [k]: v } }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5 MB'); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new window.Image();
          img.onload = () => {
            const max = 512;
            const scale = Math.min(1, max / Math.max(img.width, img.height));
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/png'));
          };
          img.onerror = reject;
          img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      setData(d => ({ ...d, logo_url: dataUrl }));
      toast.success('Logo ready — click Save to publish');
    } catch { toast.error('Failed to process image'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/branding', data);
      toast.success('Branding published. Players will see it next page load.');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const setStat = (k, v) => setData(d => ({ ...d, stats: { ...d.stats, [k]: v } }));
  const setSocial = (k, v) => setData(d => ({ ...d, socials: { ...d.socials, [k]: v } }));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="branding-page">
      <div>
        <h2 className="text-xl font-semibold">Platform Branding</h2>
        <p className="text-sm text-gray-400">Controls the player header logo/name and the full brand profile page opened when players click the logo.</p>
      </div>

      {/* Preview */}
      <div className="rounded-2xl p-5 border" style={{ background: '#050B1C', borderColor: 'rgba(244,180,0,0.45)' }}>
        <p className="text-xs text-gray-400 mb-3 uppercase tracking-wider">Header Preview</p>
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center overflow-hidden" style={{ background: data.logo_url ? 'transparent' : 'linear-gradient(180deg, #22FF88, #00C853)', boxShadow: data.logo_url ? 'none' : '0 8px 22px rgba(34,255,136,0.35)' }}>
            {data.logo_url ? <img src={data.logo_url} alt="logo" className="w-full h-full object-contain" /> : <Crown className="w-7 h-7 text-[#02140A]" />}
          </div>
          <div>
            <p className="display text-base font-extrabold tracking-wide uppercase text-white">{data.brand_name || 'TeamLobby'}</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/70">{data.brand_tagline || 'Player Lounge'}</p>
          </div>
        </div>
      </div>

      {/* Logo upload */}
      <div className="glass-card rounded-xl p-5">
        <label className="text-sm font-semibold mb-2 block">Brand Logo</label>
        <div className="flex items-center gap-3 flex-wrap">
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="branding-logo-file" />
          <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#1F2937] border border-white/10 text-white hover:bg-[#273341]" data-testid="branding-upload-btn">
            <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Processing...' : data.logo_url ? 'Replace Logo' : 'Upload Logo'}
          </Button>
          {data.logo_url && (
            <Button type="button" onClick={() => setData(d => ({ ...d, logo_url: '' }))} className="bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/20"><Trash2 className="w-4 h-4 mr-1" /> Remove</Button>
          )}
        </div>
        <Input value={data.logo_url.startsWith('data:') ? '' : data.logo_url} onChange={(e) => setData(d => ({ ...d, logo_url: e.target.value }))} className="bg-[#0B0E14] border-white/10 mt-3 text-xs" placeholder="...or paste a logo URL (https://...)" />
      </div>

      {/* Name / tagline / description / verified */}
      <div className="glass-card rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FFD93D]" /> Brand Identity</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Brand Name"><Input value={data.brand_name} onChange={(e) => setData(d => ({ ...d, brand_name: e.target.value }))} placeholder="TeamLobby" className="bg-[#0B0E14] border-white/10" data-testid="branding-name" /></Field>
          <Field label="Tagline"><Input value={data.brand_tagline} onChange={(e) => setData(d => ({ ...d, brand_tagline: e.target.value }))} placeholder="Player Lounge" className="bg-[#0B0E14] border-white/10" data-testid="branding-tagline" /></Field>
        </div>
        <Field label="Description (shown on brand profile page)">
          <textarea value={data.description || ''} onChange={(e) => setData(d => ({ ...d, description: e.target.value }))} placeholder="One short paragraph about your platform — shown under the logo on the brand profile page." rows={3} className="w-full rounded-md bg-[#0B0E14] border border-white/10 text-white p-3 text-sm" data-testid="branding-description" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer select-none">
          <input type="checkbox" checked={!!data.verified} onChange={(e) => setData(d => ({ ...d, verified: e.target.checked }))} className="w-4 h-4 accent-blue-500" data-testid="branding-verified" />
          <span>Show "Official Brand" verified badge</span>
        </label>
      </div>

      {/* Stats */}
      <div className="glass-card rounded-xl p-5">
        <h3 className="text-sm font-semibold mb-3">Brand Stats (3 highlights on profile page)</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Followers (e.g. 25K+)"><Input value={data.stats?.followers || ''} onChange={(e) => setStat('followers', e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="25K+" /></Field>
          <Field label="Games (e.g. 120+)"><Input value={data.stats?.games || ''} onChange={(e) => setStat('games', e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="120+" /></Field>
          <Field label="Rewards Claimed (e.g. 10M+)"><Input value={data.stats?.rewards_claimed || ''} onChange={(e) => setStat('rewards_claimed', e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="10M+" /></Field>
        </div>
      </div>

      {/* Socials */}
      <div className="glass-card rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold">Social & Support Links (shown on brand profile)</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {SOCIAL_FIELDS.map(f => (
            <Field key={f.key} label={f.label}>
              <Input value={data.socials?.[f.key] || ''} onChange={(e) => setSocial(f.key, e.target.value)} placeholder={f.placeholder} className="bg-[#0B0E14] border-white/10" data-testid={`branding-social-${f.key}`} />
            </Field>
          ))}
        </div>
        <p className="text-[11px] text-gray-500 flex items-center gap-1 pt-1"><ImageIcon className="w-3 h-3" /> Leave blank to hide any row on the profile page.</p>
      </div>

      {/* Landing page Support card (glowing) */}
      <div className="glass-card rounded-xl p-5 space-y-3" data-testid="branding-support-card">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Landing Page · Need Help card</h3>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={data.support?.enabled !== false} onChange={(e) => setSupport('enabled', e.target.checked)} /> Visible
          </label>
        </div>
        <p className="text-[11px] text-gray-500">Glowing card shown on the landing page for players who forgot their credentials. Fill only the channels you actually monitor.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Headline"><Input value={data.support?.headline || ''} onChange={(e) => setSupport('headline', e.target.value)} placeholder="Forgot your username or password? We're here to help." className="bg-[#0B0E14] border-white/10" data-testid="support-headline" /></Field>
          <Field label="Sub-headline"><Input value={data.support?.subheadline || ''} onChange={(e) => setSupport('subheadline', e.target.value)} placeholder="Reach us on any channel — we reply 24/7." className="bg-[#0B0E14] border-white/10" data-testid="support-subheadline" /></Field>
          <Field label="Telegram URL"><Input value={data.support?.telegram_url || ''} onChange={(e) => setSupport('telegram_url', e.target.value)} placeholder="https://t.me/yourhandle" className="bg-[#0B0E14] border-white/10" data-testid="support-telegram-url" /></Field>
          <Field label="WhatsApp URL"><Input value={data.support?.whatsapp_url || ''} onChange={(e) => setSupport('whatsapp_url', e.target.value)} placeholder="https://wa.me/15551234567" className="bg-[#0B0E14] border-white/10" data-testid="support-whatsapp-url" /></Field>
          <Field label="Facebook Page URL"><Input value={data.support?.facebook_url || ''} onChange={(e) => setSupport('facebook_url', e.target.value)} placeholder="https://facebook.com/yourpage" className="bg-[#0B0E14] border-white/10" data-testid="support-facebook-url" /></Field>
          <Field label="Messenger URL"><Input value={data.support?.messenger_url || ''} onChange={(e) => setSupport('messenger_url', e.target.value)} placeholder="https://m.me/yourpage" className="bg-[#0B0E14] border-white/10" data-testid="support-messenger-url" /></Field>
          <Field label="Discord URL"><Input value={data.support?.discord_url || ''} onChange={(e) => setSupport('discord_url', e.target.value)} placeholder="https://discord.gg/xyz" className="bg-[#0B0E14] border-white/10" data-testid="support-discord-url" /></Field>
          <Field label="Support email"><Input value={data.support?.email || ''} onChange={(e) => setSupport('email', e.target.value)} placeholder="support@yoursite.com" className="bg-[#0B0E14] border-white/10" data-testid="support-email" /></Field>
          <Field label="Phone / SMS"><Input value={data.support?.phone || ''} onChange={(e) => setSupport('phone', e.target.value)} placeholder="+1 555 123 4567" className="bg-[#0B0E14] border-white/10" data-testid="support-phone" /></Field>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#D4AF37] text-black font-bold" data-testid="branding-save">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save & Publish'}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-gray-400 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
